import _ from 'lodash';
import { ChangeEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { ContactDto, JobDto, LineItemDto, OrderDto, OrderLineItemDto } from '../../../dtos';
import { useGetJobByIdQuery, useGetMaterialQuery, useUpdateDispatchAffectedByYardAndOnsiteTimeMutation } from '../../../store/generated/generatedApi';
import { LineItemTypes, QuoteType, areDateTimesEqual, emptyGuid, hasChanged, setOrderByRequirementValidation, toEasternDateKey } from '../../../util';
import { useLineItemConverter } from '../../../util/useLineItemConverter';
import { useMaterialDuplicateChecker } from '../../../util/useMaterialDuplicateChecker';
import { IFormFieldValidationConfig, IFormProps, createValidatorConfig, isNotBlank, runFieldValidation, runFormValidation } from '../../CoreLib/library';

export interface IOrderFormValues {
    orderNumber?: number;
    orderDate?: Date | null;
    clientId?: string;
    jobId: string;
    job?: JobDto;
    quoteId?: string;
    orderLineItems: OrderLineItemDto[];
    isActive: boolean;
    poNumber: string;
    customerJobNumber: string;
    contact?: ContactDto;
    orderedByContact?: ContactDto;
    contactId?: string;
    orderedByContactId?: string;
    isTaxable?: boolean;
}

export const DEFAULT_ORDER: OrderDto = {
    id: emptyGuid,
    clientId: emptyGuid,
    jobId: emptyGuid,
    quoteId: emptyGuid,
    orderNumber: undefined,
    orderDate: new Date(),
    isActive: true,
    createdOn: new Date(),
    orderLineItems: [],
    poNumber: '',
    customerJobNumber: '',
    contact: undefined,
    orderedByContact: undefined,
    contactId: emptyGuid,
    orderedByContactId: emptyGuid,
    isTaxable: undefined,
};

const ORDER_VALIDATION_CONFIG = new Map<keyof IOrderFormValues, IFormFieldValidationConfig>([
    ['orderDate', createValidatorConfig([isNotBlank], 'Order Date')],
    ['poNumber', createValidatorConfig([isNotBlank], 'PO Number')],
]);

/** Sort order line items by order of addition (orderLineItemNumber then createdOn) and normalize orderLineItemNumber (1, 2, 3, ...). */
function sortAndRenumberOrderLineItems(items: OrderLineItemDto[]): OrderLineItemDto[] {
    const sorted = [...items].sort((a, b) => {
        const na = a.orderLineItemNumber ?? Number.MAX_SAFE_INTEGER;
        const nb = b.orderLineItemNumber ?? Number.MAX_SAFE_INTEGER;
        if (na !== nb) return na - nb;
        const ca = a.createdOn ? new Date(a.createdOn).getTime() : 0;
        const cb = b.createdOn ? new Date(b.createdOn).getTime() : 0;
        return ca - cb;
    });
    return sorted.map((item, i) => ({
        ...item,
        orderLineItemNumber: i + 1,
    }));
}

/** Mirrors isNotModifiable in OrderLineItemTable: these rows are rendered with every field disabled. */
function isLineItemModifiable(lineItem: OrderLineItemDto): boolean {
    return !lineItem.canceledOn && !lineItem.isOrderInReview;
}

function buildLineItemValidationConfig(lineItem: OrderLineItemDto): Map<keyof OrderLineItemDto, IFormFieldValidationConfig> {
    var validationConfig = new Map<keyof OrderLineItemDto, IFormFieldValidationConfig>();
    // Quantity needs the requirement just as much as Loads does: without it the row saves with a NULL
    // LoadOrQuantityReq and the Dispatch screen's Requirement column has nothing to show (RK-7255).
    // The Quantity case is skipped on rows the form disables outright, so an older line saved without
    // a requirement cannot lock an order out of saving with a value nobody can type. Those rows are
    // corrected by the BackfillQuantityOrderedLoadOrQuantityReq migration instead.
    setOrderByRequirementValidation(validationConfig, lineItem, isLineItemModifiable(lineItem));
    validationConfig.set('poNumber', createValidatorConfig([isNotBlank], 'PO Number'));
    // if interval count is not undefined, then dispatching interval time must be defined and vice versa
    if (lineItem.intervalCount) {
        validationConfig.set('dispatchingIntervalTime', createValidatorConfig([isNotBlank], 'Dispatching Interval Time'));
    }

    if (lineItem.dispatchingIntervalTime) {
        validationConfig.set('intervalCount', createValidatorConfig([isNotBlank], 'Interval Count'));
    }
    return validationConfig;
}

export function useOrderForm(props: IFormProps<OrderDto>, jobId: string) {
    const { save, cancel, initValues } = props;
    const { data: job, isLoading: isJobLoading } = useGetJobByIdQuery({ id: jobId! });

    const [formOrderNumber] = useState<number | undefined>(initValues?.orderNumber);
    const [formIsActive, setFormIsActive] = useState(initValues?.isActive ?? true);
    const [formOrderDate] = useState<Date | null | undefined>(initValues?.orderDate ? new Date(initValues?.orderDate) : new Date());
    const [formOrderLineItems, setFormOrderLineItems] = useState<OrderLineItemDto[]>(
        sortAndRenumberOrderLineItems(initValues?.orderLineItems.map((x) => x as OrderLineItemDto) ?? [])
    );
    const [formPoNumber, setFormPoNumber] = useState(initValues?.poNumber || '');
    const [formJobNumber, setFormJobNumber] = useState(initValues?.customerJobNumber || '');
    const [isChanged, setIsChanged] = useState(false);

    const [formContact, setFormContact] = useState<ContactDto | undefined>(initValues?.contact);
    const [formOrderedByContact, setFormOrderedByContact] = useState<ContactDto | undefined>(initValues?.orderedByContact);

    const [formQuote, setFormQuote] = useState(initValues?.quote);
    const [isSaved, setIsSaved] = useState(false);
    const formCustomer = useMemo(() => job?.currentQuote?.customer, [job?.currentQuote?.customer]);
    const [formIsTaxable, setFormIsTaxable] = useState(initValues?.isTaxable ?? false);

    const [isQuickAddMaterialLineItemDialogOpen, setIsQuickAddMaterialLineItemDialogOpen] = useState(false);
    const [selectedSiteHaulingLineItem, setSelectedSiteHaulingLineItem] = useState<LineItemDto | undefined>();

    const [fieldErrors, setFieldErrors] = useState<Map<keyof IOrderFormValues, string>>(new Map([['orderDate', '']]));
    const [lineItemFieldErrors, setLineItemFieldErrors] = useState<Map<keyof OrderLineItemDto, string>[]>([]);
    const [orderLineItemsCache, setOrderLineItemsCache] = useState<OrderLineItemDto[]>(initValues?.orderLineItems ?? []);
    // The version of the order this screen is editing. It's sent with every save so the server can reject
    // a save from an out-of-date screen instead of overwriting newer changes (RK-8176). It deliberately
    // does not follow initValues refetches: the rest of the form state doesn't either.
    const [formConcurrencyVersion, setFormConcurrencyVersion] = useState(initValues?.concurrencyVersion);

    const { convertQuoteLineItemToOrderLineItem } = useLineItemConverter(formQuote);
    const [showAffectedDispatchesDialog, setShowAffectedDispatchesDialog] = useState(false);

    // Get materials to look up materialId by code (since materialId is not always in the lineItem structure)
    const { data: materialsData } = useGetMaterialQuery({
        sorting: ['CODE+'],
        page: 0,
        pageSize: 10000,
        includeInactive: false
    });
    const [getAffectedDispatches, { data: affectedDispatches, isLoading: isGettingAffectedDispatches }] = useUpdateDispatchAffectedByYardAndOnsiteTimeMutation();

    const createOrderLineItemErrorsMap = () => {
        return new Map<keyof OrderLineItemDto, string>([
            ['orderBy', ''],
            ['loadOrQuantityReq', ''],
            ['equipmentRequired', ''],
            ['internalMemo', ''],
            ['poNumber', ''],
            ['dispatchingIntervalTime', ''],
            ['intervalCount', ''],
        ]);
    };

    useEffect(() => {
        if (initValues?.orderLineItems) {
            var errorMessagePlaceholders = initValues?.orderLineItems.map(() => createOrderLineItemErrorsMap());
            setLineItemFieldErrors(errorMessagePlaceholders);
        }
    }, [formOrderLineItems, initValues?.orderLineItems]);

    // Sync cancel-related fields from initValues into form state when initValues updates (e.g. after order-level cancel/restore refetch)
    useEffect(() => {
        if (!initValues?.orderLineItems?.length) return;
        setFormOrderLineItems((current) => {
            const initById = new Map(initValues.orderLineItems!.map((item) => [item.id, item]));
            return current.map((formItem) => {
                const initItem = initById.get(formItem.id);
                if (!initItem) return formItem;
                return {
                    ...formItem,
                    canceledOn: initItem.canceledOn,
                    canceledByUserId: initItem.canceledByUserId,
                    cancellationReason: initItem.cancellationReason,
                    isCancelable: initItem.isCancelable,
                    canceledByUser: initItem.canceledByUser
                };
            });
        });
    }, [initValues?.id, initValues?.orderLineItems]);

    const getCurrentFormValues = useCallback((): IOrderFormValues => {
        return {
            jobId: jobId,
            isActive: formIsActive,
            orderNumber: formOrderNumber,
            orderDate: formOrderDate,
            orderLineItems: formOrderLineItems,
            poNumber: formPoNumber,
            customerJobNumber: formJobNumber,
            contact: formContact,
            orderedByContact: formOrderedByContact,
            contactId: formContact?.id,
            orderedByContactId: formOrderedByContact?.id,
            quoteId: formQuote?.id,
            isTaxable: formIsTaxable,
        };
    }, [
        formContact,
        formIsActive,
        formIsTaxable,
        formOrderDate,
        formOrderLineItems,
        formOrderNumber,
        formOrderedByContact,
        formPoNumber,
        formJobNumber,
        formQuote?.id,
        jobId
    ]);

    const validateForm = useCallback(() => {
        const formValues = getCurrentFormValues();
        const validationResult = runFormValidation<Partial<IOrderFormValues>>(formValues, ORDER_VALIDATION_CONFIG);
        setFieldErrors(validationResult.errorMessages);

        const lineItemValidationResults = formOrderLineItems.map((lineItem) =>
            runFormValidation<OrderLineItemDto>(lineItem, buildLineItemValidationConfig(lineItem))
        );
        const lineItemErrorMessages = lineItemValidationResults.map((lir) => lir.errorMessages);
        setLineItemFieldErrors(lineItemErrorMessages);

        return validationResult.isValid && lineItemValidationResults.every((lir) => lir.isValid);
    }, [formOrderLineItems, getCurrentFormValues]);

    const validateField = useCallback(
        (fieldName: keyof IOrderFormValues) => {
            const validationConfig = ORDER_VALIDATION_CONFIG.get(fieldName);
            if (validationConfig) {
                const formValues = getCurrentFormValues();
                const fieldValue = formValues[fieldName];
                const { errorMessage } = runFieldValidation(fieldValue, validationConfig);

                if (errorMessage !== fieldErrors.get(fieldName)) {
                    const updatedFieldErrors = _.cloneDeep(fieldErrors);
                    updatedFieldErrors.set(fieldName, errorMessage);
                    setFieldErrors(updatedFieldErrors);
                }
            }
        },
        [fieldErrors, getCurrentFormValues]
    );

    const isFormDirty = useCallback(() => {
        if (isSaved) {
            return false;
        }
        var isDirty = hasChanged(formIsActive, initValues?.isActive);
        isDirty = isDirty || hasChanged(formOrderNumber, initValues?.orderNumber);
        isDirty = isDirty || hasChanged(formPoNumber, initValues?.poNumber ?? formQuote?.poNumber);
        isDirty = isDirty || isChanged;
        return isDirty;
    }, [
        formIsActive,
        formOrderNumber,
        formPoNumber,
        formQuote?.poNumber,
        initValues?.isActive,
        initValues?.orderNumber,
        initValues?.poNumber,
        isChanged,
        isSaved,
    ]);

    const handleOrderLineItemsChange = useCallback((value: OrderLineItemDto[] | ((currentValues: OrderLineItemDto[]) => OrderLineItemDto[])) => {
        setFormOrderLineItems(value);
        setIsChanged(true);
    }, []);

    const handleIsActiveChange = useCallback((_: ChangeEvent<HTMLInputElement>, isChecked: boolean) => {
        setFormIsActive(isChecked);
        setIsChanged(true);
    }, []);

    const handleRemoveLineItem = useCallback(
        (index: number) => {
            const updatedLineErrors = [...lineItemFieldErrors];
            updatedLineErrors.splice(index, 1);
            setLineItemFieldErrors(updatedLineErrors);

            const updatedLineItems = formOrderLineItems.filter((_, i) => i !== index);
            setFormOrderLineItems(sortAndRenumberOrderLineItems(updatedLineItems));
            setIsChanged(true);
        },
        [formOrderLineItems, lineItemFieldErrors]
    );

    const handleContactChange = useCallback((value?: ContactDto) => {
        setFormContact(value);
        setIsChanged(true);
    }, []);

    const handleOrderedByContactChange = useCallback((value?: ContactDto) => {
        setFormOrderedByContact(value);
        setIsChanged(true);
    }, []);

    const handleIsTaxableChange = useCallback((_: ChangeEvent<HTMLInputElement>, isChecked: boolean) => {
        setFormIsTaxable(isChecked);
        setIsChanged(true);
    }, []);

    const [defaultErrorMessageMap] = useState(
        new Map<keyof OrderLineItemDto, string>([
            ['orderBy', ''],
            ['loadOrQuantityReq', ''],
            ['equipmentRequired', ''],
            ['haulToAddressLine1', ''],
            ['haulToCountry', ''],
            ['haulToCity', ''],
            ['haulToState', ''],
            ['haulToZipCode', ''],
            ['haulFromAddressLine1', ''],
            ['haulFromCountry', ''],
            ['haulFromCity', ''],
            ['haulFromState', ''],
            ['haulFromZipCode', ''],
            ['poNumber', ''],
            ['dispatchingIntervalTime', ''],
            ['intervalCount', ''],
        ])
    );

    const createNewOrderLineItem = useCallback(
        async (lineItem: LineItemDto, placeholderLineNumber: number): Promise<OrderLineItemDto> => {
            let newOrderLineItem = await convertQuoteLineItemToOrderLineItem(lineItem, placeholderLineNumber);
            if (!newOrderLineItem.isCustomPoNumber) {
                newOrderLineItem.poNumber = formPoNumber;
            }
            if (!newOrderLineItem.isCustomerJobNumberCustom) {
                newOrderLineItem.customerJobNumber = formJobNumber;
            }
            return newOrderLineItem;
        },
        [convertQuoteLineItemToOrderLineItem, formPoNumber, formJobNumber]
    );

    const handleAddNewLineItem = useCallback(
        async (lineItem: LineItemDto) => {
            if (lineItem.type === LineItemTypes.SiteHauling) {
                if (formQuote?.type === QuoteType.Standard) {
                    setIsQuickAddMaterialLineItemDialogOpen(true);
                    setSelectedSiteHaulingLineItem(lineItem);
                }
            } else {
                var updatedLineErrors = _.cloneDeep(lineItemFieldErrors);
                var newErrorMessageMap = defaultErrorMessageMap;
                updatedLineErrors.push(newErrorMessageMap);
                setLineItemFieldErrors(updatedLineErrors);

                var updatedLineItems = _.cloneDeep(formOrderLineItems);
                const nextNumber = updatedLineItems.length === 0 ? 1 : Math.max(...updatedLineItems.map((x) => x.orderLineItemNumber ?? 0)) + 1;
                const newOrderLineItem = await createNewOrderLineItem(lineItem, nextNumber);
                updatedLineItems.push(newOrderLineItem);
                setFormOrderLineItems(sortAndRenumberOrderLineItems(updatedLineItems));
                setIsChanged(true);
            }
        },
        [createNewOrderLineItem, defaultErrorMessageMap, formOrderLineItems, formQuote?.type, lineItemFieldErrors]
    );

    const handleAddNewLineItems = useCallback(
        async (lineItems: LineItemDto[] | undefined) => {
            if (!lineItems) {
                return;
            }
            const filteredLineItems = lineItems
                .filter((x) => x.type !== LineItemTypes.SiteHauling) // Site Hauling line items are handled separately
                .slice()
                .sort((a, b) => {
                    const orderA = a.displayOrder ?? Number.MAX_SAFE_INTEGER;
                    const orderB = b.displayOrder ?? Number.MAX_SAFE_INTEGER;
                    if (orderA !== orderB) return orderA - orderB;
                    const createdA = a.createdOn ? new Date(a.createdOn).getTime() : 0;
                    const createdB = b.createdOn ? new Date(b.createdOn).getTime() : 0;
                    return createdA - createdB;
                });
            var updatedLineErrors = _.cloneDeep(lineItemFieldErrors);
            for (let index = 0; index < filteredLineItems.length; index++) {
                var newErrorMessageMap = defaultErrorMessageMap;
                updatedLineErrors.push(newErrorMessageMap);
            }
            setLineItemFieldErrors(updatedLineErrors);

            var updatedLineItems = _.cloneDeep(formOrderLineItems);
            for (let index = 0; index < filteredLineItems.length; index++) {
                const nextNumber = updatedLineItems.length === 0 ? 1 : Math.max(...updatedLineItems.map((x) => x.orderLineItemNumber ?? 0)) + 1;
                const newOrderLineItem = await createNewOrderLineItem(filteredLineItems[index], nextNumber);
                updatedLineItems.push(newOrderLineItem);
            }

            setFormOrderLineItems(sortAndRenumberOrderLineItems(updatedLineItems));
            setIsChanged(true);
        },
        [createNewOrderLineItem, defaultErrorMessageMap, formOrderLineItems, lineItemFieldErrors]
    );

    const haveOrderLineItemTimesChanged = useCallback(() => {
        const current = formOrderLineItems;
        const previous = orderLineItemsCache;
        const modifiedItems: OrderLineItemDto[] = [];

        const minLength = Math.min(current.length, previous.length);
        for (let i = 0; i < minLength; i++) {
            const a = current[i];
            const b = previous[i];
            if (
                !areDateTimesEqual(a.yardTime, b.yardTime) ||
                (!a.yardTime && (!areDateTimesEqual(a.onsiteTime, b.onsiteTime))) ||
                a.dispatchingIntervalTime !== b.dispatchingIntervalTime ||
                a.intervalCount !== b.intervalCount
            ) {
                modifiedItems.push(a);
            }
        }

        return modifiedItems;
    }, [formOrderLineItems, orderLineItemsCache]);

    const handleSave = useCallback(async () => {
        const isFormValid = validateForm();
        if (isFormValid) {
            const formValues = getCurrentFormValues();
            const updatedUserRecord = {
                ...(initValues ?? DEFAULT_ORDER),
                ...formValues,
                concurrencyVersion: formConcurrencyVersion,
            };
            let returned: OrderDto | void;
            try {
                returned = await (save as (v: OrderDto) => Promise<OrderDto | void>)(updatedUserRecord as OrderDto);
            } catch {
                // The view reports the failure; leave the form dirty so the user's changes aren't treated as saved.
                return;
            }
            if (returned?.concurrencyVersion) {
                setFormConcurrencyVersion(returned.concurrencyVersion);
            }
            setIsChanged(false);
            setIsSaved(true);
            // Apply normalized order line items from server (DisplayOrder, OrderLineItemNumber) when returned
            if (returned?.orderLineItems?.length !== undefined) {
                const items = returned.orderLineItems.map((x) => x as OrderLineItemDto);
                setFormOrderLineItems(items);
                setOrderLineItemsCache(items);
            } else {
                setOrderLineItemsCache(formValues.orderLineItems);
            }
            const modifiedOrderLineItems = haveOrderLineItemTimesChanged();
            if (modifiedOrderLineItems.length > 0) {
                const isInPast = formValues.orderDate && toEasternDateKey(formValues.orderDate) < toEasternDateKey(new Date()); // We don't allow orders on past (Eastern) days to be edited
                if (!isInPast) {
                    await getAffectedDispatches(modifiedOrderLineItems).unwrap().then((data) => {
                        if (data && data.length > 0) {
                            setShowAffectedDispatchesDialog(true);
                        }
                    });
                }
            }
        }
    }, [validateForm, getCurrentFormValues, initValues, formConcurrencyVersion, save, haveOrderLineItemTimesChanged, getAffectedDispatches]);

    /**
     * Takes the version the server returned for a cancel/restore this screen made outside of Save. The screen sends its
     * own version with that request, and the server returns the order's new version only if the screen was current
     * before the change (otherwise nothing), so the user's own action doesn't make their next Save conflict, while a
     * screen someone else had already made stale stays stale.
     */
    const adoptVersionFromOwnChange = useCallback((version?: string | null) => {
        if (version) {
            setFormConcurrencyVersion(version);
        }
    }, []);

    const handleCancel = useCallback(() => {
        cancel();
    }, [cancel]);

    const handlePoNumberChange = useCallback(
        (value: string) => {
            setFormPoNumber(value);
            // Update all line items with isCustomPoNumber set to false with the new PO number
            const updatedLineItems = formOrderLineItems.map((x) => {
                if (!x.isCustomPoNumber) {
                    return {
                        ...x,
                        poNumber: value,
                    };
                }
                return x;
            });
            setFormOrderLineItems(updatedLineItems);
        },
        [formOrderLineItems]
    );

    const handleJobNumberChange = useCallback(
        (value: string) => {
            setFormJobNumber(value);
            // Update all line items with isCustomJobNumber set to false with the new Job number
            const updatedLineItems = formOrderLineItems.map((x) => {
                if (!x.isCustomerJobNumberCustom) {
                    return {
                        ...x,
                        customerJobNumber: value,
                    };
                }
                return x;
            });
            setFormOrderLineItems(updatedLineItems);
        },
        [formOrderLineItems]
    );

    // Use reusable hook for duplicate checking
    const { getExcludedMaterialIdsForHaulingLineItem } = useMaterialDuplicateChecker({
        lineItems: formOrderLineItems,
        materialsData,
    });

    return {
        isFormDirty,
        handleSave,
        handleCancel,
        fieldErrors,
        handleOrderLineItemsChange,
        handleIsActiveChange,
        handleAddNewLineItem,
        handleAddNewLineItems,
        handleRemoveLineItem,
        handleContactChange,
        handleOrderedByContactChange,
        handleIsTaxableChange,
        handlePoNumberChange,
        handleJobNumberChange,
        formContact,
        formOrderedByContact,
        formIsActive,
        formOrderNumber,
        formOrderDate,
        formOrderLineItems,
        formPoNumber,
        formJobNumber,
        formQuote,
        formCustomer,
        formIsTaxable,
        setFormQuote,
        validateField,
        job,
        isJobLoading,
        lineItemFieldErrors,
        isQuickAddMaterialLineItemDialogOpen,
        selectedSiteHaulingLineItem,
        setIsQuickAddMaterialLineItemDialogOpen,
        affectedDispatches,
        isGettingAffectedDispatches,
        showAffectedDispatchesDialog,
        setShowAffectedDispatchesDialog,
        getExcludedMaterialIdsForHaulingLineItem,
        formConcurrencyVersion,
        adoptVersionFromOwnChange
    };
}
